
import * as React from "react";
import {
  format,
  subYears,
  getYear,
  getMonth,
  getDate,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { UseFormReturn } from "react-hook-form";

interface DatePickerFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  description?: string;
}

export function DatePickerField({
  form,
  name,
  label,
  description,
}: DatePickerFieldProps) {
  const today = new Date();
  const maxDate = subYears(today, -10); // Must be at least 18 years old
  const minDate = subYears(today, 120); // Reasonable minimum date

  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [step, setStep] = React.useState<"year" | "month" | "day">("year");

  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = React.useState<number | null>(null);

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setStep("month");
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setStep("day");
  };

  const handleDateSelect = (date: Date, onChange: (date: Date) => void) => {
    onChange(date);
    setIsCalendarOpen(false);
    setStep("year");
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  const handleWheel = (e: React.WheelEvent) => {
    // Stop propagation to prevent parent scroll areas from capturing the scroll
    e.stopPropagation();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Stop propagation to prevent dialog from closing when clicking scrollbars
    e.stopPropagation();
  };

  const handleClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent dialog from closing when clicking scrollbars
    e.stopPropagation();
  };

  const renderYearGrid = () => {
    const startYear = maxDate.getFullYear(); // e.g. 2007
    const endYear = minDate.getFullYear(); // e.g. 1905

    const years = [];
    for (let y = startYear; y >= endYear; y--) {
      years.push(y);
    }

    return (
      <div 
        className="grid grid-cols-4 gap-2 p-3 max-h-[300px] overflow-y-auto"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {years.map((year) => (
          <Button
            key={year}
            variant="ghost"
            className="w-full"
            onClick={() => handleYearSelect(year)}
          >
            {year}
          </Button>
        ))}
      </div>
    );
  };

  const renderMonthGrid = () => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString("default", { month: "short" })
    );

    return (
      <div 
        className="grid grid-cols-4 gap-2 p-3"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {months.map((month, index) => (
          <Button
            key={month}
            variant="ghost"
            className="w-full"
            onClick={() => handleMonthSelect(index)}
          >
            {month}
          </Button>
        ))}
      </div>
    );
  };

  const renderDayGrid = (onChange: (date: Date) => void) => {
    if (selectedYear === null || selectedMonth === null) return null;

    const start = startOfMonth(new Date(selectedYear, selectedMonth));
    const end = endOfMonth(start);
    const days = eachDayOfInterval({ start, end });

    return (
      <div 
        className="grid grid-cols-7 gap-2 p-3"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {days.map((day) => {
          const disabled = day > maxDate || day < minDate;
          return (
            <Button
              key={day.toISOString()}
              variant="ghost"
              className={cn(
                "w-full",
                disabled && "opacity-30 pointer-events-none"
              )}
              onClick={() => handleDateSelect(day, onChange)}
            >
              {getDate(day)}
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <Popover
            open={isCalendarOpen}
            onOpenChange={(open) => {
              setIsCalendarOpen(open);
              if (!open) {
                setStep("year");
                setSelectedYear(null);
                setSelectedMonth(null);
              }
            }}
          >
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full pl-3 text-left font-normal",
                    !field.value && "text-muted-foreground"
                  )}
                >
                  {field.value ? (
                    format(new Date(field.value), "PPP")
                  ) : (
                    <span>Select date</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[60]" align="start">
              {step === "year" && renderYearGrid()}
              {step === "month" && renderMonthGrid()}
              {step === "day" && renderDayGrid(field.onChange)}
            </PopoverContent>
          </Popover>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
